"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";

import type { ReplayMetricKey } from "@/entities/replay-metrics";
import { scoreColor } from "@/shared/lib/score-color";

/**
 * KAN-876: радар поведенческого профиля (эпик KAN-804).
 *
 * Отдельный компонент, а не переиспользованный `BehaviorRadarCard` из профиля
 * пользователя: тот показывает метрики за ВСЕ турниры и без перцентилей, здесь —
 * один турнир и сравнение внутри роли. Совпадает только форма, а не смысл осей;
 * переиспользование вслепую свело бы две разные величины к одной картинке.
 *
 * ОСЬ БЕЗ ПЕРЦЕНТИЛЯ НЕ РИСУЕТСЯ ТОЧКОЙ. Её не с чем сравнивать (KAN-725), а точка на
 * оси — это утверждение о месте внутри роли. Поставить её в ноль значило бы сказать
 * «хуже всех сравнимых», поставить в середину — «ровно как все»; оба утверждения
 * выдуманы. Такая ось остаётся спицей с приглушённой подписью, а форма строится по
 * измеренным.
 */

/** Геометрия из макета: центр, радиус, кольца. */
const CX = 180;
const CY = 160;
const R = 108;
const RINGS = [1, 0.75, 0.5, 0.25];

/**
 * Пол для вершины, 4% от радиуса. Чисто визуальный: без него балл около нуля
 * схлопывает фигуру в точку и она перестаёт читаться. На числа не влияет — их
 * показывает таблица осей рядом.
 */
const MIN_VERTEX = 4;

/** Меньше трёх измеренных осей — многоугольника не существует. */
const MIN_AXES_FOR_SHAPE = 3;

export interface RadarAxis {
  key: ReplayMetricKey;
  label: string;
  /** Балл 0–100. null — перцентиля нет, ось не участвует в форме. */
  score: number | null;
  /** Готовая строка сырого значения с единицей. */
  raw: string;
  /** «5-й из 8» либо null, если место посчитать не из чего. */
  rankNote: string | null;
}

function point(index: number, total: number, radius: number): [number, number] {
  const angle = -Math.PI / 2 + (index * 2 * Math.PI) / total;
  return [CX + Math.cos(angle) * radius, CY + Math.sin(angle) * radius];
}

function polygon(points: [number, number][]): string {
  return points.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
}

export function ProfileRadar({ axes }: { axes: RadarAxis[] }) {
  const t = useTranslations("Profile.behaviorMetrics");
  const [hovered, setHovered] = useState<number | null>(null);

  const total = axes.length;
  const measured = axes.filter((a) => a.score != null);

  const ringPoints = RINGS.map((factor) =>
    polygon(axes.map((_, i) => point(i, total, R * factor))),
  );

  const vertex = (index: number, score: number): [number, number] =>
    point(index, total, (R * Math.max(score, MIN_VERTEX)) / 100);

  const shape =
    measured.length >= MIN_AXES_FOR_SHAPE
      ? polygon(
          axes
            .map((a, i) => (a.score != null ? vertex(i, a.score) : null))
            .filter((p): p is [number, number] => p !== null),
        )
      : null;

  const tip = hovered != null ? (axes[hovered] ?? null) : null;
  const tipScore = tip?.score ?? null;
  const tipPoint =
    hovered != null && tipScore != null ? vertex(hovered, tipScore) : null;

  return (
    <div className="relative min-w-0 max-w-[470px] flex-1 basis-[380px] rounded-[13px] border border-surface-inset-border bg-surface-inset px-2.5 py-3.5">
      {tip && tipPoint && tipScore != null ? (
        <div
          className="pointer-events-none absolute z-[3] -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-[10px] border border-border bg-background px-[11px] py-2 shadow-lg"
          style={{
            left: `${(tipPoint[0] / 516) * 100 + 12}%`,
            top: `${(tipPoint[1] / 340) * 100}%`,
          }}
        >
          <div className="text-[11px] font-semibold text-muted-foreground">{tip.label}</div>
          <div className="mt-0.5 flex items-baseline gap-[7px]">
            <span
              className="font-heading text-lg font-semibold leading-none tabular-nums"
              style={{ color: scoreColor(tipScore) }}
            >
              {Math.round(tipScore)}
            </span>
            <span className="text-xs tabular-nums text-muted-foreground">{tip.raw}</span>
          </div>
          {tip.rankNote ? (
            <div className="mt-[3px] text-[10px] text-muted-foreground">{tip.rankNote}</div>
          ) : null}
        </div>
      ) : null}

      <svg viewBox="-62 0 516 340" className="block h-auto w-full overflow-visible">
        <defs>
          {/* KAN-900: оранжевая заливка вместо `--primary`. Центр темнее, края к
              светло-золотому — так фигура читается как объём, а не как белёсое
              пятно, и совпадает с металлом первого места в пьедестале.

              Цвета заданы явно, а не токеном: это не поверхность и не текст, а
              единственная в продукте градиентная заливка фигуры. Токен под неё
              завёл бы сущность с одним потребителем, а `--primary` здесь давал
              ровно ту белёсость, из-за которой правку и завели. */}
          <radialGradient id="glRadarFill" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#a1400c" stopOpacity="0.92" />
            <stop offset="55%" stopColor="#e07a1c" stopOpacity="0.72" />
            <stop offset="100%" stopColor="#fbbf4a" stopOpacity="0.42" />
          </radialGradient>
        </defs>

        {ringPoints.map((points, i) => (
          <polygon
            key={points}
            points={points}
            fill="none"
            stroke="var(--border)"
            strokeWidth="1"
            strokeOpacity={i === 0 ? 0.9 : 0.45}
          />
        ))}

        {axes.map((axis, i) => {
          const [x, y] = point(i, total, R);
          return (
            <line
              key={axis.key}
              x1={CX}
              y1={CY}
              x2={x.toFixed(1)}
              y2={y.toFixed(1)}
              stroke="var(--border)"
              strokeWidth="1"
              strokeOpacity="0.55"
            />
          );
        })}

        {shape ? (
          <polygon
            points={shape}
            fill="url(#glRadarFill)"
            stroke="#fbbf4a"
            strokeWidth="2.5"
            strokeLinejoin="round"
          />
        ) : null}

        {axes.map((axis, i) => {
          if (axis.score == null) return null;
          const [x, y] = vertex(i, axis.score);
          return (
            <circle
              key={axis.key}
              cx={x.toFixed(1)}
              cy={y.toFixed(1)}
              r={hovered === i ? 7 : 4}
              fill="#fbbf4a"
              stroke="var(--card)"
              strokeWidth="2"
            />
          );
        })}

        {axes.map((axis, i) => {
          const [x, y] = point(i, total, R + 26);
          const dx = x - CX;
          const active = hovered === i;
          // Ось без перцентиля подписана приглушённо: подпись остаётся, потому что
          // метрика существует, но её место внутри роли неизвестно.
          const dimmed = axis.score == null;

          return (
            <text
              key={axis.key}
              x={x.toFixed(1)}
              y={(y + 4).toFixed(1)}
              textAnchor={Math.abs(dx) < 12 ? "middle" : dx > 0 ? "start" : "end"}
              fontSize={15}
              fontWeight={active ? 700 : 600}
              fill={active ? "var(--foreground)" : "var(--muted-foreground)"}
              fillOpacity={dimmed ? 0.55 : 1}
            >
              {axis.label}
            </text>
          );
        })}

        {axes.map((axis, i) => {
          if (axis.score == null) return null;
          const [x, y] = vertex(i, axis.score);
          return (
            <circle
              key={axis.key}
              cx={x.toFixed(1)}
              cy={y.toFixed(1)}
              r={26}
              fill="transparent"
              className="cursor-pointer"
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
            />
          );
        })}
      </svg>

      {/* Оговорка обязательна: без неё фигура по пяти осям неотличима от фигуры по
          семи, и читатель решит, что измерено всё. */}
      {shape && measured.length < total ? (
        <p className="mt-2 px-1 text-[11px] text-muted-foreground">{t("someAxesHidden")}</p>
      ) : null}
    </div>
  );
}
